// libraries
import * as React from "react";
// styles
import * as SC from "./Styled";
// types & models
import { IATCameraDto, IATVideoItemDto, IATVideoPageDto } from "@algo/network-manager/models/v4";
// components
import CameraInfoOverlay from "../../camera-info-overlay/CameraInfoOverlay";
import { VideoJs } from "../local-videoJS";
import { GenericToastError } from "~/components/app-content/toast-error";
// constants
import { fairplay_certificateUrl } from "~/constants";
import { getSub } from "~/authentication/oidcConfig";

const STANDARD_VJS_CONFIG = {
    autoplay: true,
    controls: false,
};

declare var __WIDEVINE_LICENSE_URL__: string;
declare var __FAIRPLAY_LICENSE_URL__: string;

export type IProps = {
    page?: IATVideoPageDto | null;
    showRouteOverlay?: boolean;
    showCityOverlay?: boolean;
    lockOverlays?: boolean;
    fullscreen?: boolean;
    isVideoboard?: boolean;
    updatePlayerMap: (key: string, player: any) => void;
    closeCallback: () => void;
};

export const VideoGrid: React.FC<IProps> = (props) => {

    const {
        page, showCityOverlay, showRouteOverlay, lockOverlays, fullscreen, isVideoboard, closeCallback
    } = props;

    const [sub, setSub] = React.useState<string | null>(null);
    const [subLoaded, setSubLoaded] = React.useState<boolean>(false);

    React.useEffect(() => {
        getSub().then((userSub) => {
            setSub(Buffer.from(userSub, 'utf-8').toString('base64'));
        });

        setSubLoaded(true);
    }, []);
    
    const [videoItems, setVideoItems] = React.useState<JSX.Element[]>([]);
    
    React.useEffect(() => {
        const loadVideoItems = async () => {
            if (page && page.items && subLoaded) {
                const items = await Promise.all(
                    page.items.map(async (item: IATVideoItemDto, index: number) => renderVideoItem(page, item, index))
                );
        
                // Filter out null values and cast the result to Element[] for proper typing
                const validItems = items.filter((item): item is React.ReactElement => item !== null);
                
                if(validItems.length > 0) {
                    // Set valid items
                    setVideoItems(validItems);
                }
                else{
                    // Show error toast and close modal
                    GenericToastError("Secure video playback could not be established.", "DRM");
                    closeCallback();
                }
            }
        };
    
        loadVideoItems(); // Trigger the async function on mount or page change
    }, [page, subLoaded]);

    const renderVideoItem = async (page: IATVideoPageDto, item: IATVideoItemDto, index: number) => {
        const config = await getStreamConfig(item);
        const camera: IATCameraDto | null = item.camera;
    
        if (!config || !camera) return null; // Return null if no config or camera is available
    
        return (
            <SC.VideoItem key={item.id || index} fullscreen={fullscreen} twoVideos={twoVideos}>
                { camera &&
                    <VideoJs
                        key={`${camera.id.toString()}-${page.placement}`}
                        id={index.toString()}
                        style={{height: "100%", width: "100%"}}
                        rounded={false} playButton={true}
                        config={config.streamConfig} videoClassNames=""
                        videoDimensions={{width: `100%`, height: `100%`}}
                        playerCallback={
                            (player: any) => {
                                props.updatePlayerMap(index.toString(), player);
                            }
                        }
                        closeCallback={closeCallback}
                        mediaType={config.mediaType}
                    />
                }

                { !isVideoboard && camera &&
                    <SC.TitleItem lockOverlays={lockOverlays}>
                        <CameraInfoOverlay camera={camera} 
                            showRouteOverlay={showRouteOverlay} showCityOverlay={showCityOverlay} />
                    </SC.TitleItem>
                }

            </SC.VideoItem>
        );
    };

    // Determine the configuration based on platform
    const getStreamConfig = async (item: IATVideoItemDto) => {
        // Get key system support once
        const { isHLS, isDASH, widevineRobustness } = await checkKeySystemSupport();

        // console.log(`HLS w/ FairPlay Supported: ${isHLS}\nMPEG-DASH w/ Widevine Support: ${isDASH}\nSupported Robustness Level: ${widevineRobustness}`);
    
        // Helper function to generate the source object
        const createSource = (url: string, type: string, keySystem: string, licenseUri: string, certificateUri?: string, widevineRobustness?: string) => ({
            src: url,
            type: type,
            keySystems: certificateUri
                ? { [keySystem]: { certificateUri, licenseUri, options: { persistentState: 'required' } } }
                : { [keySystem]: { url: licenseUri, videoRobustness: widevineRobustness } }
        });
    
        // Handle HLS if supported
        if (isHLS && item?.camera?.playbackUrls?.hls) {
            const licenseUrl = getLicenseUrl("hls", item.camera.id);
            return { 
                streamConfig: {
                    ...STANDARD_VJS_CONFIG,
                    poster: item?.camera?.snapshotImageUrl,
                    sources: [
                        createSource(item.camera.playbackUrls.hls, 'application/x-mpegURL', 
                            'com.apple.fps.1_0', licenseUrl, fairplay_certificateUrl)
                    ],
                    html5: {
                        vhs: {
                            withCredentials: true,
                        },
                    },
                },
                mediaType: "HLS"
            };
        }
    
        // Handle DASH if supported
        if (isDASH && item?.camera?.playbackUrls?.dash) {
            const licenseUrl = getLicenseUrl("dash", item.camera.id);
            return {
                streamConfig: {
                    ...STANDARD_VJS_CONFIG,
                    poster: item?.camera?.snapshotImageUrl,
                    sources: [
                        createSource(item.camera.playbackUrls.dash, 'application/dash+xml', 
                            'com.widevine.alpha', licenseUrl, undefined, widevineRobustness)
                    ],
                },
                mediaType: "MPEG-DASH"
            }
        }
    
        // Return null if neither HLS nor DASH is supported
        return null;
    };

    // Helper function to detect keysystem support
    const checkKeySystemSupport = async (): Promise<{ isHLS: boolean; isDASH: boolean; widevineRobustness: string | undefined }> => {
        const robustnessLevels = [
            'HW_SECURE_ALL', 
            'HW_SECURE_DECODE',
            'SW_SECURE_DECODE',
            'SW_SECURE_CRYPTO'
        ];
    
        const checkSupport = async (keySystem: string, config: MediaKeySystemConfiguration[]): Promise<boolean> => {
            try {
                await navigator.requestMediaKeySystemAccess(keySystem, config);
                return true;
            } catch {
                return false;
            }
        };
    
        const checkWidevineSupport = async (): Promise<string | undefined> => {
            for (const robustness of robustnessLevels) {
                const widevineConfig: MediaKeySystemConfiguration[] = [
                    {
                        initDataTypes: ['cenc'],
                        videoCapabilities: [
                            { contentType: 'video/mp4; codecs="avc1.42E01E"', robustness: robustness },
                            { contentType: 'video/webm; codecs="vp9"', robustness: robustness }
                        ],
                    }
                ];
                
                const isSupported = await checkSupport('com.widevine.alpha', widevineConfig);
                if (isSupported) {
                    return robustness;
                }
            }
            return undefined;
        };
    
        const checkFairplaySupport = async (): Promise<boolean> => {
            const fairplayConfig: MediaKeySystemConfiguration[] = [
                {
                    initDataTypes: ['cenc'],
                    videoCapabilities: [{ contentType: 'application/x-mpegURL' }],
                }
            ];
            
            return await checkSupport('com.apple.fps.1_0', fairplayConfig);
        };
    
        const widevineRobustness = await checkWidevineSupport();
        const isFairplaySupported = await checkFairplaySupport();
    
        return {
            isHLS: isFairplaySupported,
            isDASH: widevineRobustness !== undefined,
            widevineRobustness: widevineRobustness
        };
    };

    const getLicenseUrl = (type: string, cameraId: number): string => {
        let licenseUrl = "";
        const userId = localStorage.getItem("userId");

        if (type === "hls") {
            if (sub) licenseUrl = `${__FAIRPLAY_LICENSE_URL__}?user_id=${userId}&cameraId=${cameraId}&sub=${sub}&application=trafficweb`;
            else licenseUrl = `${__FAIRPLAY_LICENSE_URL__}?user_id=${userId}&cameraId=${cameraId}&application=trafficweb`;
        }
        else if (type === "dash") {
            if (sub) licenseUrl = `${__WIDEVINE_LICENSE_URL__}&user_id=${userId}&cameraId=${cameraId}&sub=${sub}&application=trafficweb`;
            else licenseUrl = `${__WIDEVINE_LICENSE_URL__}&user_id=${userId}&cameraId=${cameraId}&application=trafficweb`;
        }
        
        return licenseUrl;
    };

    // Handle cases where no valid stream is found
    if (!page) return null;

    const oneColumn: boolean = isVideoboard ? page?.columnCount === 1 : page?.items.length && page?.items.length > 1 ? false : true;
    const twoVideos: boolean = page?.items.length === 2 ? true : false;

    return (
        <SC.VideoGrid oneColumn={oneColumn} fullscreen={fullscreen} twoVideos={twoVideos}>
            {videoItems}
        </SC.VideoGrid>
    );
};

export default VideoGrid;
